/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { RefCallback, RefObject } from "react";

import {
  ClickAwayListener,
  Fade,
  Grow,
  Paper,
  Popper,
  Typography
} from "@mui/material";

import { ExpandMoreIcon } from "@dotnet/telematics-design-system";

import GridNavigationArrows from "../../shared/components/GridNavigationArrows";
import {
  expandMoreStyle,
  getSlideStyle,
  header,
  headerCSS,
  picker,
  slideAnimations
} from "../../shared/styles/useDatePickerStyles";
import { SLIDE_CLASS, VIEW_MODE } from "../../shared/types";
import YearView from "../../shared/YearView/YearView";

interface BaseDayPickersPopoverProps {
  open: boolean;
  anchorRef: RefObject<HTMLDivElement | null>;
  pickerRef: RefCallback<HTMLDivElement>;
  maxPickerHeight: number | null;
  viewMode: VIEW_MODE;
  viewVisible: boolean;
  slideClass: SLIDE_CLASS;
  year: number;
  isAtMinMonth: boolean;
  isAtMaxMonth: boolean;
  getHeaderLabel: () => string;
  onClose: () => void;
  onHeaderClick: () => void;
  onNavigateBack: () => void;
  onNavigateForward: () => void;
  onYearSelect: (year: number) => void;
  minDate?: moment.Moment;
  maxDate?: moment.Moment;
  headerSlot?: React.ReactNode;
  timeComponent?: React.ReactNode;
  children: React.ReactNode;
}

const BaseDayPickersPopover = ({
  open,
  anchorRef,
  pickerRef,
  maxPickerHeight,
  viewMode,
  viewVisible,
  slideClass,
  year,
  isAtMinMonth,
  isAtMaxMonth,
  getHeaderLabel,
  onClose,
  onHeaderClick,
  onNavigateBack,
  onNavigateForward,
  onYearSelect,
  minDate,
  maxDate,
  headerSlot,
  timeComponent,
  children
}: BaseDayPickersPopoverProps) => (
  <>
    {open && (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1299
        }}
      />
    )}
    <Popper
      open={open}
      anchorEl={anchorRef.current}
      placement="bottom-start"
      transition
      style={{ zIndex: 1300 }}
      data-testid="base-day-pickers-popper"
    >
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin:
              placement === "top-start" ? "bottom left" : "top left"
          }}
          timeout={{ enter: 250, exit: 250 }}
        >
          <Paper elevation={0}>
            <ClickAwayListener onClickAway={onClose}>
              <div
                ref={pickerRef}
                css={css(picker, {
                  ...(maxPickerHeight !== null && {
                    maxHeight: `${maxPickerHeight}px`,
                    overflowY: "auto"
                  })
                })}
              >
                <div>{timeComponent}</div>
                <div css={css({ padding: "16px 8px" })}>
                  <style>{slideAnimations}</style>

                  <div css={css(header)}>
                    <Typography
                      variant="body1Medium"
                      onClick={onHeaderClick}
                      css={css(headerCSS)}
                    >
                      <span>{getHeaderLabel()}</span>
                      <ExpandMoreIcon
                        style={expandMoreStyle(viewMode === VIEW_MODE.Day)}
                      />
                    </Typography>

                    {viewMode === VIEW_MODE.Day && (
                      <GridNavigationArrows
                        onBack={onNavigateBack}
                        onForward={onNavigateForward}
                        backDisabled={isAtMinMonth}
                        forwardDisabled={isAtMaxMonth}
                      />
                    )}
                  </div>

                  <Fade in={viewVisible} timeout={{ enter: 160, exit: 160 }}>
                    <div
                      style={
                        viewMode === VIEW_MODE.Year ||
                        viewMode === VIEW_MODE.Month
                          ? undefined
                          : { overflow: "hidden" }
                      }
                    >
                      {headerSlot}
                      <div style={getSlideStyle(slideClass)}>
                        {viewMode === VIEW_MODE.Year ? (
                          <YearView
                            year={year}
                            currentYear={new Date().getFullYear()}
                            minDate={minDate}
                            maxDate={maxDate}
                            onYearSelect={onYearSelect}
                          />
                        ) : (
                          children
                        )}
                      </div>
                    </div>
                  </Fade>
                </div>
              </div>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  </>
);

export default BaseDayPickersPopover;
